<template>
  <div>
    <WarRoomGrid>
      <div slot="content">
        <WarRoomPostBreadcrumbs :post="currentPost" class="war-room-post-show-page__breadcrumbs" />
        <div class="war-room-post-show-page__content">
          <WarRoomPost :post="currentPost" />
          <NuxtChild />
          <ClientOnly v-if="!$auth.loggedIn">
            <GateWidget
              :is-member-post="currentPost.members_only"
              :title="currentPost.members_only ? 'Join now to<br/> read the full thread' : undefined"
            />
          </ClientOnly>
        </div>
        <WarRoomRelatedPosts
          class="war-room-post-show-page__related-posts"
          :parent-post-id="currentPost.id"
          :post-slug="currentPost.slug"
        />
      </div>

      <template slot="sidebar">
        <WarRoomRules v-if="$auth.loggedIn" class="war-room-post-show-page__rules" :expanded="false" />
        <WarRoomSignUpWidget v-else />
      </template>
    </WarRoomGrid>

    <ClientOnly>
      <WarRoomShareModal />
      <WarRoomConfirmRemovePost />
    </ClientOnly>

    <PortalTarget name="comment-image" transition="fade" />
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import type { MetaInfo } from 'vue-meta';
import { mapGetters } from 'vuex';
import { format, parse } from 'date-fns';
import { DEFAULT_OG_IMAGE } from '@/constants/defaultMeta';
import getSeoDescription from '@/components/partials/war-room/getSeoDescription';
import WarRoomPost from '@/components/partials/war-room/WarRoomPost.vue';
import WarRoomRules from '@/components/partials/war-room/WarRoomRules.vue';
import WarRoomGrid from '@/components/partials/war-room/WarRoomGrid/WarRoomGrid.vue';
import WarRoomPostBreadcrumbs from '@/components/partials/war-room/WarRoomPostBreadcrumbs/WarRoomPostBreadcrumbs.vue';
import WarRoomRelatedPosts from '@/components/partials/war-room/WarRoomRelatedPosts.vue';
import WarRoomShareModal from '@/components/partials/war-room/WarRoomShare/WarRoomShareModal.vue';
import WarRoomConfirmRemovePost from '@/components/partials/war-room/WarRoomConfirmRemovePost.vue';
import GateWidget from '@/components/partials/war-room/GateWidget/GateWidget.vue';
import WarRoomSignUpWidget from '~/components/partials/war-room/WarRoomSignUp/WarRoomSignUpWidget.vue';
import { makeUrl } from '~/components/utils/browser/url';
import { POSTS_PAGE } from '~/constants';
import type { ITopic } from '~/types/common';

const POST_PAGE_PATH_PREFIX = POSTS_PAGE.path + '/';

export default Vue.extend({
  name: 'WarRoomPostShowPage',
  components: {
    WarRoomSignUpWidget,
    WarRoomRules,
    WarRoomPost,
    WarRoomPostBreadcrumbs,
    WarRoomGrid,
    WarRoomRelatedPosts,
    WarRoomShareModal,
    WarRoomConfirmRemovePost,
    GateWidget,
  },
  middleware({ route, query, redirect }) {
    // to support old links
    if (query.comment_id) {
      redirect(301, `${POST_PAGE_PATH_PREFIX}${route.params.postSlug}/comments/${query.comment_id}`);
    }
  },
  async fetch({ store, $auth, route, app }) {
    try {
      await Promise.all(
        [
          store.dispatch('warRoom/fetchPost', { postSlug: route.params.postSlug }),
          store.dispatch('warRoom/fetchRelatedPosts', { postSlug: route.params.postSlug }),
          // We need that data for proper title
          store.dispatch('warRoom/fetchTopics'),
          store.dispatch('warRoom/fetchPostComments', {
            query: route.query,
            postSlug: route.params.postSlug,
            page: 1,
            commentsMutation: 'setCommentTree',
          }),
          $auth.loggedIn && store.dispatch('warRoom/fetchSidebar'),
        ].filter(Boolean),
      );
    } catch (e) {
      app.$utils.nuxtErrorPage(e as Error);
      app.$sentryUtils.error(e);
    }
  },
  head(): MetaInfo {
    const url = makeUrl(`${POST_PAGE_PATH_PREFIX}${this.$route.params.postSlug}`);
    const image = this.currentPost.og_image_url;
    const postTitle = this.currentPost.content?.data?.title;
    const category = this.currentPost.category.title;
    const topics = this.currentPost.topic_ids
      ?.map((postTopicId: number) => {
        return this.topics?.find(({ id }: ITopic) => postTopicId === id)?.name;
      })
      .join(', ');
    const isShittyPost = this.currentPost.rating <= -2;

    const dealDate = this.currentPost?.content?.data?.deal?.close_date
      ? format(parse(this.currentPost.content.data.deal.close_date, 'yyyy-mm-dd', new Date()), 'mm-dd-yyyy')
      : null;

    const title = [
      postTitle,
      ...(category ? [`[${category}]`] : []),
      ...(topics ? [`| ${topics}`] : []),
      ...(dealDate ? [`${dealDate}`] : []),
    ].join(' ');

    return {
      title,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: getSeoDescription(this.$route.params.postSlug) || this.currentPost.seo_description,
        },
        {
          hid: 'og:image',
          property: 'og:image',
          content: image || DEFAULT_OG_IMAGE,
        },
        {
          hid: 'twitter:card',
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          hid: 'robots',
          name: 'robots',
          content: isShittyPost ? 'noindex' : 'index',
        },
      ],
      link: [
        {
          hid: 'canonical',
          rel: 'canonical',
          href: url,
        },
      ],
    };
  },
  computed: {
    ...mapGetters('warRoom', ['currentPost', 'topics']),
  },
  mounted() {
    this.$analytics.logEvent('war-room__post__page__load', { post_id: this.currentPost.id });
  },
});
</script>

<style scoped lang="scss">
.war-room-post-show-page {
  &__breadcrumbs {
    margin: 0 0 16px;

    @include mq('sm') {
      margin: 0 0 12px;
    }
  }

  &__related-posts {
    margin-top: 20px;
  }

  &__rules {
    padding: 0 24px;
    margin-top: 24px;

    @include mq('sm') {
      padding: 0;
      margin-top: 0;
    }
  }

  &__content {
    padding: 16px;
    background: var(--theme-color-card);
    box-shadow: var(--theme-elevation-01);
    border-radius: 0;
    margin: 0 -16px;

    @include mq('sm') {
      padding: 28px 24px;
      border-radius: 8px;
      margin: 0;
    }
  }
}
</style>
